import { defineStore } from "pinia";
import { useStorage } from "@vueuse/core";
import {
  CognitoIdentityProviderClient,
  ConfirmForgotPasswordCommand,
  ForgotPasswordCommand,
  GetUserCommand,
  ResendConfirmationCodeCommand,
  InitiateAuthCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import type {
  AuthenticationResultType,
  InitiateAuthCommandOutput,
} from "@aws-sdk/client-cognito-identity-provider";
import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from "@aws-sdk/client-cognito-identity";
import jwt_decode from "jwt-decode";
import * as Sentry from "@sentry/browser";
import type { User } from "best-of-awards-api";

import { useAwardStore } from "@/stores";
import { publicBoaHttp } from "@/boa-api-client";

const TOKENS_STORAGE_KEY = "boa-tokens";

const providerClient = new CognitoIdentityProviderClient({
  region: import.meta.env.VITE_AWS_REGION,
});

const identityClient = new CognitoIdentityClient({
  region: import.meta.env.VITE_AWS_REGION,
});

type Tokens = AuthenticationResultType;

type Credentials = {
  AccessKeyId?: string;
  SecretKey?: string | undefined;
  SessionToken?: string | undefined;
  IdentityId: string | undefined;
  Expiration?: Date | undefined;
};

type IdClaims = {
  "cognito:groups": string[];
};

export enum Role {
  Admin = "Admin",
  Candidate = "Candidate",
  Unauthorized = "Unauthorized",
}

type State = {
  authenticating: boolean;
  authenticated: boolean;
  latestClientVersion: string;
  codeRequired: boolean;
  credentials: Credentials;
  refreshIntervalId: any;
  reloading: boolean;
  status: string;
  tokens: any;
  user: User;
  authChallengeData: InitiateAuthCommandOutput | null;
  errorMessage: string;
  initialSignupData: {
    candidateId?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  };
};

const defaultUser: User = {
  awardId: "",
  candidateId: "",
  candidateVerified: false,
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  roles: [Role.Unauthorized],
  role: Role.Unauthorized,
  stripeCustomerId: "",
  title: "",
  Username: "",
};

const defaultCredentials = {
  AccessKeyId: "",
  IdentityId: "",
  SecretKey: "",
  SessionToken: "",
  Expiration: new Date(),
};

const defaultTokens: Tokens = {
  AccessToken: "",
  ExpiresIn: 0,
  IdToken: "",
  RefreshToken: "",
};

const storedTokens = useStorage<Tokens>(TOKENS_STORAGE_KEY, defaultTokens);

export default defineStore("auth", {
  state: (): State => ({
    authenticating: false,
    authenticated: false,
    latestClientVersion: "",
    codeRequired: false,
    credentials: { ...defaultCredentials },
    refreshIntervalId: null,
    reloading: false,
    status: "",
    tokens: storedTokens,
    user: { ...defaultUser },
    authChallengeData: null,
    errorMessage: "",
    initialSignupData: {
      candidateId: "",
      email: "",
      firstName: "",
      lastName: "",
      phone: "",
    },
  }),
  getters: {
    isAdmin: (state) => state.user.roles.includes(Role.Admin),
    isCandidate: (state) => state.user.roles.includes(Role.Candidate),
    fullName: (state) =>
      `${state.user.firstName || ""} ${state.user.lastName || ""}`,
    candidateId: (state) => state.user.candidateId,
  },
  actions: {
    async getUser(): Promise<User> {
      const response = await providerClient.send(
        new GetUserCommand({ AccessToken: this.tokens.AccessToken })
      );
      const { UserAttributes, Username = "" } = response;
      const firstName =
        UserAttributes?.find((attr) => attr.Name === "given_name")?.Value ?? "";
      const lastName = UserAttributes?.find(
        (attr) => attr.Name === "family_name"
      )?.Value;
      const email =
        UserAttributes?.find((attr) => attr.Name === "email")?.Value ?? "";
      const email_verifiedAttr = UserAttributes?.find(
        (attr) => attr.Name === "email_verified"
      )?.Value;
      const email_verified = email_verifiedAttr === "true";
      const candidateVerifiedAttr = UserAttributes?.find(
        (attr) => attr.Name === "custom:candidate_verified"
      )?.Value;
      const candidateVerified = candidateVerifiedAttr === "1";
      const candidateId = UserAttributes?.find(
        (attr) => attr.Name === "custom:candidate_id"
      )?.Value;
      const awardId = UserAttributes?.find(
        (attr) => attr.Name === "custom:award_id"
      )?.Value;
      const title =
        UserAttributes?.find((attr) => attr.Name === "custom:title")?.Value ??
        "";
      const phone = UserAttributes?.find(
        (attr) => attr.Name === "phone_number"
      )?.Value;
      const stripeCustomerId =
        UserAttributes?.find(
          (attr) => attr.Name === "custom:stripe_customer_id"
        )?.Value ?? "";
      const user = {
        awardId: awardId || "",
        candidateId: candidateId || "",
        candidateVerified,
        email,
        email_verified,
        firstName,
        lastName: lastName || "",
        phone: phone || "",
        roles: [Role.Unauthorized],
        role: Role.Unauthorized,
        stripeCustomerId,
        title,
        Username,
      };
      return user;
    },
    async getId() {
      const { IdentityId } = await identityClient.send(
        new GetIdCommand({
          IdentityPoolId: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID,
          Logins: {
            [import.meta.env.VITE_COGNITO_USER_POOL || ""]: this.tokens.IdToken,
          },
        })
      );
      return IdentityId;
    },
    async getCredentialsForIdentity(): Promise<Credentials> {
      const IdentityId = await this.getId();
      if (!IdentityId) {
        return { ...defaultCredentials };
      }
      const { Credentials } = await identityClient.send(
        new GetCredentialsForIdentityCommand({
          IdentityId,
          Logins: {
            [import.meta.env.VITE_COGNITO_USER_POOL || ""]: this.tokens.IdToken,
          },
        })
      );
      return {
        ...Credentials,
        IdentityId,
      };
    },
    async refreshTokens() {
      if (!this.tokens.RefreshToken) {
        return null;
      }
      try {
        const response = await providerClient.send(
          new InitiateAuthCommand({
            ClientId: import.meta.env.VITE_COGNITO_APP_CLIENT_ID,
            AuthFlow: "REFRESH_TOKEN_AUTH",
            AuthParameters: {
              REFRESH_TOKEN: this.tokens.RefreshToken,
            },
          })
        );
        return response;
      } catch (error) {
        return null;
      }
    },
    async login({
      username = "",
      password = "",
    }: {
      username: string;
      password: string;
    }): Promise<{
      authenticated: boolean;
      message: string;
      resetRequired?: boolean;
      forceResetRequired?: boolean;
    }> {
      this.authenticating = true;
      if (username) {
        Sentry.setUser({ username, email: username });
      }
      try {
        let AuthenticationResult: AuthenticationResultType | undefined;
        if (username && password) {
          const response = await providerClient.send(
            new InitiateAuthCommand({
              ClientId: import.meta.env.VITE_COGNITO_APP_CLIENT_ID,
              AuthFlow: "USER_PASSWORD_AUTH",
              AuthParameters: {
                USERNAME: username.toLocaleLowerCase(),
                PASSWORD: password,
              },
            })
          );
          if (response.ChallengeName === "NEW_PASSWORD_REQUIRED") {
            this.authChallengeData = response;
            return {
              forceResetRequired: true,
              authenticated: false,
              message: "",
            };
          }
          AuthenticationResult = response.AuthenticationResult;
        } else {
          const refreshResponse = await this.refreshTokens();
          if (!refreshResponse) {
            this.logout();
            return { authenticated: false, message: "" };
          }
          AuthenticationResult = refreshResponse.AuthenticationResult;
        }

        if (AuthenticationResult) {
          this.tokens.AccessToken = AuthenticationResult.AccessToken;
          this.tokens.ExpiresIn = AuthenticationResult.ExpiresIn;
          this.tokens.IdToken = AuthenticationResult.IdToken;
          if (AuthenticationResult.RefreshToken) {
            this.tokens.RefreshToken = AuthenticationResult.RefreshToken;
          }
          let roles = [Role.Unauthorized];
          let role = Role.Unauthorized;
          if (AuthenticationResult.IdToken) {
            const claims = jwt_decode<IdClaims>(AuthenticationResult.IdToken);
            roles = claims["cognito:groups"] as Role[];
            if (roles.includes(Role.Admin)) {
              role = Role.Admin;
            } else if (roles.includes(Role.Candidate)) {
              role = Role.Candidate;
            }
            const credentials = await this.getCredentialsForIdentity();
            this.credentials = credentials;
          }

          if (AuthenticationResult.AccessToken) {
            const user = await this.getUser();
            Sentry.setUser({ username: user.email, ...user });
            this.user = {
              ...user,
              role,
              roles,
            };
          }

          this.authenticated = true;
          if (!this.refreshIntervalId) {
            setInterval(() => {}, 5000);
          }
          return { authenticated: true, message: "" };
        } else {
          return {
            authenticated: false,
            message: "Invalid email or password.",
          };
        }
      } catch (error: any) {
        this.authenticated = false;
        console.error(error);
        if (error.name === "PasswordResetRequiredException") {
          return { authenticated: false, resetRequired: true, message: "" };
        }
        return { authenticated: false, message: (error as any).message };
      } finally {
        this.authenticating = false;
      }
    },
    async setPassword({
      email,
      candidateId,
      password,
    }: {
      email: string;
      candidateId: string;
      password: string;
    }) {
      const response: { success: boolean } = await publicBoaHttp
        .post("candidates/activate-account", {
          json: {
            email,
            candidateId,
            password,
          },
        })
        .json();
      return response;
    },
    async logout() {
      this.tokens = { ...defaultTokens };
      this.clear();
    },
    setInitialSignupData({
      candidateId,
      email,
      firstName,
      lastName,
      phone,
    }: {
      candidateId: string;
      email: string;
      firstName: string;
      lastName: string;
      phone: string;
    }) {
      this.initialSignupData = {
        candidateId,
        email,
        firstName,
        lastName,
        phone,
      };
    },
    async signup({
      email,
      phone,
      firstName,
      lastName,
      candidateId,
      password,
    }: {
      email: string;
      phone: string;
      firstName: string;
      lastName: string;
      candidateId: string;
      password: string;
    }) {
      try {
        const awardStore = useAwardStore();
        const response = await providerClient.send(
          new SignUpCommand({
            ClientId: import.meta.env.VITE_COGNITO_APP_CLIENT_ID,
            Username: email.toLocaleLowerCase(),
            Password: password,
            UserAttributes: [
              {
                Name: "family_name",
                Value: lastName.trim(),
              },
              {
                Name: "given_name",
                Value: firstName.trim(),
              },
              {
                Name: "email",
                Value: email.trim().toLocaleLowerCase(),
              },
              {
                Name: "phone_number",
                Value: phone.trim(),
              },
              {
                Name: "custom:candidate_id",
                Value: candidateId,
              },
              {
                Name: "custom:award_id",
                Value: awardStore.activeAward?.settings.id,
              },
            ],
          })
        );
        return {
          success: true,
          codeDeliveryMedium:
            response.CodeDeliveryDetails?.DeliveryMedium || "",
        };
      } catch (error) {
        if ((error as any).name === "UsernameExistsException") {
          return {
            message: "An account with the given email already exists.",
            userExists: true,
          };
        }
        if ((error as any).name === "InvalidPasswordException") {
          return {
            message: "Password must be at least 8 characters.",
            badPassword: true,
          };
        }
        return { message: (error as any).message };
      }
    },
    async confirmSignup({ email, code }: { email: string; code: string }) {
      if (!code || !email) {
        return;
      }
      try {
        await providerClient.send(
          new ConfirmSignUpCommand({
            ClientId: import.meta.env.VITE_COGNITO_APP_CLIENT_ID,
            ConfirmationCode: code.trim(),
            Username: email.toLocaleLowerCase(),
          })
        );
        return { success: true };
      } catch (error) {
        if ((error as any).name === "NotAuthorizedException") {
          return { success: true };
        }
        return { success: false, message: (error as any).message };
      }
    },
    async forgotPassword({ email }: { email: string }) {
      if (!email) {
        return;
      }
      try {
        await providerClient.send(
          new ForgotPasswordCommand({
            ClientId: import.meta.env.VITE_COGNITO_APP_CLIENT_ID,
            Username: email.toLocaleLowerCase(),
          })
        );
        return { success: true };
      } catch (error) {
        return { success: false, message: (error as any).message };
      }
    },
    async verifyForgotPasswordCode({ email, code, newPassword }) {
      if (!code) {
        return;
      }
      try {
        await providerClient.send(
          new ConfirmForgotPasswordCommand({
            ClientId: import.meta.env.VITE_COGNITO_APP_CLIENT_ID,
            ConfirmationCode: code.trim(),
            Password: newPassword,
            Username: email.toLocaleLowerCase(),
          })
        );
        return { success: true };
      } catch (error) {
        return { success: false, message: (error as any).message };
      }
    },
    async forceResetPassword({ newPassword }: { newPassword: string }) {
      try {
        this.authenticating = true;
        const response = await providerClient.send(
          new RespondToAuthChallengeCommand({
            ClientId: import.meta.env.VITE_COGNITO_APP_CLIENT_ID,
            ChallengeName: "NEW_PASSWORD_REQUIRED",
            ChallengeResponses: {
              NEW_PASSWORD: newPassword,
              USERNAME:
                this.authChallengeData?.ChallengeParameters?.USER_ID_FOR_SRP!,
            },
            Session: this.authChallengeData?.Session!,
          })
        );
        const AuthenticationResult = response.AuthenticationResult;
        if (AuthenticationResult) {
          this.tokens.AccessToken = AuthenticationResult.AccessToken;
          this.tokens.ExpiresIn = AuthenticationResult.ExpiresIn;
          this.tokens.IdToken = AuthenticationResult.IdToken;
          if (AuthenticationResult.RefreshToken) {
            this.tokens.RefreshToken = AuthenticationResult.RefreshToken;
          }
          const { authenticated } = await this.login({
            username: "",
            password: "",
          });
          if (!authenticated) {
            return {
              authenticated: false,
              message: "There was an error while setting your new password.",
            };
          }
          return {
            authenticated: true,
          };
        }
      } catch (error: any) {
        this.authenticated = false;
        console.error(error);
        if (error.name === "PasswordResetRequiredException") {
          return { authenticated: false, resetRequired: true, message: "" };
        }
        return { authenticated: false, message: (error as any).message };
      } finally {
        this.authenticating = false;
      }
    },
    async resendCode({ email }: { email: string }) {
      if (!email) {
        return;
      }
      try {
        await providerClient.send(
          new ResendConfirmationCodeCommand({
            ClientId: import.meta.env.VITE_COGNITO_APP_CLIENT_ID,
            Username: email.toLocaleLowerCase(),
          })
        );
        return { success: true };
      } catch (error) {
        return { success: false, message: (error as any).message };
      }
    },
    clear() {
      this.$reset();
    },
  },
});
